import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Box, Typography, Chip, Button, Tooltip, CircularProgress } from "@mui/material";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChannelTabs from '../../../common_components/ChannelTabs';
import HoverTooltip from '../../../common_components/hoverOverlay/HoverTooltip';
import { TextInput } from '../../../common_components/FormInputs';
import { Save } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { updateClientGoals, selectYearMonth } from '../../../redux/goalSlice';
import { formatCurrency, TYPE_LABEL_MAP, labelStyle, valueStyle, formatValue } from '../shared';
import { ApiCall } from '../../../helper/axios';
import { buildTrackerRows, buildTrackerTotal, calcMetricCardValues } from '../../AccountPerformanceReport/tabs/performanceTrackerUtils';
import IOSSwitch from '../../../common_components/IOSSwitch';
const getMonthLabel = (ym, offset = 0) => {
    if (!ym)
        return '';
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(y, m - 1 + offset, 1);
    const month = d.toLocaleString('en-us', { month: 'short' });
    const year = String(d.getFullYear()).slice(-2);
    return `${month} ${year}`;
};
const GoalExpandedContent = ({ row, goalType, onActionClick }) => {
    const dispatch = useDispatch();
    const currentYearMonth = useSelector(selectYearMonth);
    const types = row.summary?.type || [];
    const [subTab, setSubTab] = useState(types[0] || "");
    const [editData, setEditData] = useState({});
    const [selectedConnectionId, setSelectedConnectionId] = useState(""); // Added
    const [isSaving, setIsSaving] = useState(false);
    const initialEditDataRef = useRef({});
    const token = useSelector((state) => state.auth?.token);
    const [prevTrendingRev, setPrevTrendingRev] = useState(null);
    const [prevActualRev, setPrevActualRev] = useState(null);
    const [currTrendingRev, setCurrTrendingRev] = useState(null);
    useEffect(() => {
        if (!row.summary?.client_id || !currentYearMonth || goalType !== 'am_projection')
            return;
        const fetchTrendingRev = async (month) => {
            try {
                const response = await ApiCall("POST", "/api/account-performance-tracker/list", { client_id: row.summary.client_id, month }, { Authorization: token || "" });
                if (response && "data" in response && response.data?.success && response.data?.data) {
                    const responseData = response.data.data;
                    const rows = buildTrackerRows(responseData.tableData, responseData.metrics, responseData.savedModifiers || {}, false, responseData.formulas);
                    const computedTotal = buildTrackerTotal(rows, responseData.tableTotal, responseData.metrics);
                    const cardValues = calcMetricCardValues(computedTotal, responseData.metrics, responseData.formulas);
                    return { trendingRev: cardValues.revMtd, actualRev: computedTotal.revenue?.mtd || 0 };
                }
            }
            catch (error) {
                console.error(`Failed to fetch trending rev for ${month}`, error);
            }
            return null;
        };
        const [y, m] = currentYearMonth.split('-').map(Number);
        const d = new Date(y, m - 1 - 1, 1);
        const prevMonthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        fetchTrendingRev(prevMonthStr).then((result) => {
            if (result) {
                setPrevTrendingRev(result.trendingRev);
                setPrevActualRev(result.actualRev);
            }
        });
        fetchTrendingRev(currentYearMonth).then((result) => {
            if (result) {
                setCurrTrendingRev(result.trendingRev);
            }
        });
    }, [row.summary?.client_id, currentYearMonth, token, goalType]);
    useEffect(() => {
        if (row.tabs) {
            const baseline = {
                prev_month_roas_or_spend: row.tabs.prev_month_roas_or_spend ?? 0,
                current_month_roas_or_spend: row.tabs.current_month_roas_or_spend ?? 0,
                paid_changes_revenue: row.tabs.paid_changes_revenue ?? 0,
                aff_am_projected_revenue: row.tabs.aff_am_projected_revenue ?? 0,
                aff_trending_projected_revenue: row.tabs.aff_trending_projected_revenue ?? 0,
                aff_budget_toggle: row.tabs.aff_budget_toggle ?? false,
                aff_samples_toggle: row.tabs.aff_samples_toggle ?? false,
            };
            setEditData(baseline);
            initialEditDataRef.current = baseline;
        }
    }, [row.tabs]);
    useEffect(() => {
        if (types.length > 0 && !types.includes(subTab)) {
            setSubTab(types[0]);
        }
    }, [types, subTab]);
    const handleEditChange = (field, value) => {
        setEditData((prev) => ({ ...prev, [field]: value }));
    };
    const handleSave = async () => {
        const baseline = initialEditDataRef.current;
        const isPaidModified = Number(editData.current_month_roas_or_spend || 0) !== Number(baseline.current_month_roas_or_spend || 0) ||
            Number(editData.paid_changes_revenue || 0) !== Number(baseline.paid_changes_revenue || 0);
        const isAffModified = Number(editData.aff_am_projected_revenue || 0) !== Number(baseline.aff_am_projected_revenue || 0) ||
            Number(editData.aff_trending_projected_revenue || 0) !== Number(baseline.aff_trending_projected_revenue || 0) ||
            Boolean(editData.aff_budget_toggle) !== Boolean(baseline.aff_budget_toggle) ||
            Boolean(editData.aff_samples_toggle) !== Boolean(baseline.aff_samples_toggle);
        if (!isPaidModified && !isAffModified) {
            return;
        }
        const payload = {
            client_id: row.summary?.client_id,
            connection_id: selectedConnectionId || undefined, // Added
            year_month: row.year_month || currentYearMonth,
        };
        // Only send the section that actually changed — each field the backend
        // sees here triggers its own Google Sheets round trip, so sending unmodified
        // fields on every save was writing (and then resyncing) both sheets every time.
        if (isPaidModified) {
            payload.current_month_roas_or_spend = Number(editData.current_month_roas_or_spend || 0);
            payload.paid_changes_revenue = Number(editData.paid_changes_revenue || 0);
        }
        if (isAffModified) {
            payload.aff_am_projected_revenue = Number(editData.aff_am_projected_revenue || 0);
            payload.aff_trending_projected_revenue = Number(editData.aff_trending_projected_revenue || 0);
            payload.aff_budget_toggle = Boolean(editData.aff_budget_toggle);
            payload.aff_samples_toggle = Boolean(editData.aff_samples_toggle);
        }
        // Saving directly moves the edited tab's goal to waiting_for_approval — there's no separate submit step.
        if (types.includes('paid_media') && isPaidModified) {
            payload.paid_approval_status = "waiting_for_approval";
        }
        if (types.includes('affiliate') && isAffModified) {
            payload.aff_approval_status = "waiting_for_approval";
        }
        setIsSaving(true);
        try {
            await dispatch(updateClientGoals(payload)).unwrap();
        }
        finally {
            setIsSaving(false);
        }
    };
    if (types.length === 0) {
        return (_jsx(Box, { sx: { p: 2, textAlign: 'center', color: '#667085' }, children: "No detailed data available." }));
    }
    const tabItems = types.map((t) => ({
        key: t,
        label: TYPE_LABEL_MAP[t] || t
    }));
    const showTabs = types.length > 1;
    const data = row.tabs || {};
    const summary = row.summary || {};
    const isClientInactive = summary.status === 'inactive';
    const isAffOrderBase = (data.aff_account_type || '').toLowerCase().includes('order') || (data.aff_type || '').toLowerCase().includes('order');
    const paidBox1LabelWidth = '150px';
    const paidBox2LabelWidth = '250px';
    const affiliateBox1LabelWidth = '180px';
    const affiliateBox2LabelWidth = '140px';
    const commissionPaidBox1LabelWidth = '190px';
    const commissionPaidBox2LabelWidth = '220px';
    const commissionAffiliateBox1LabelWidth = '190px';
    const commissionAffiliateBox2LabelWidth = '160px';
    const renderField = (label, value, options) => {
        let displayValue = formatValue(value);
        if (options?.isCurrency) {
            displayValue = formatCurrency(value);
        }
        else if (options?.isFormattedNumber) {
            displayValue = value !== null && value !== undefined && value !== ''
                ? Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '-';
        }
        else if (options?.isPercent) {
            displayValue = formatValue(value, '%');
        }
        return (_jsxs(Box, { sx: {
                display: 'flex',
                alignItems: (options?.isEditable || options?.isToggle || options?.isChip || options?.isGrowthRate || Array.isArray(value)) ? 'center' : 'flex-start',
                py: options?.isEditable ? 0.25 : 0.4,
                gap: 1,
            }, children: [_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        width: options?.labelWidth || '220px',
                        minWidth: options?.labelWidth || '220px',
                        flexShrink: 0,
                    }, children: [_jsx(Typography, { sx: labelStyle, children: label }), options?.tooltipText && (_jsx(Tooltip, { title: options.tooltipText, placement: "top", arrow: true, children: _jsx(HelpOutlineIcon, { sx: { ml: 0.5, fontSize: '16px', color: 'text.secondary', cursor: 'pointer' } }) }))] }), _jsx(Box, { sx: { flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }, children: options?.isEditable ? (_jsx(Tooltip, { title: options.disabledTooltip || '', placement: "top", arrow: true, disableHoverListener: !(options.disabled && options.disabledTooltip), children: _jsx(Box, { sx: {
                                maxWidth: '120px',
                                '& .form-input': {
                                    fontSize: '13px',
                                    py: '4px',
                                    px: '8px',
                                    height: '30px'
                                }
                            }, children: _jsx(TextInput, { size: "sm", value: editData[options.editField || ""], onChange: (e) => handleEditChange(options.editField || "", e.target.value), disabled: options.disabled }) }) })) : options?.isToggle ? (_jsx(Tooltip, { title: options.disabledTooltip || '', placement: "top", arrow: true, disableHoverListener: !(options.disabled && options.disabledTooltip), children: _jsx(Box, { sx: { ml: -1 }, children: _jsx(IOSSwitch, { checked: Boolean(editData[options.editField || ""]), onChange: (e) => handleEditChange(options.editField || "", e.target.checked), disabled: options.disabled }) }) })) : options?.isGrowthRate && value !== null && value !== undefined ? (() => {
                        const v = Number(value);
                        const isPos = v >= 0;
                        return (_jsxs(Box, { sx: {
                                backgroundColor: isPos ? "#ecfdf3" : "#fef3f2",
                                borderRadius: "16px",
                                px: 1,
                                py: 0.2,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5
                            }, children: [_jsxs(Typography, { sx: {
                                        fontFamily: "Inter",
                                        fontWeight: 500,
                                        fontSize: "12px",
                                        color: isPos ? "#027a48" : "#b42318"
                                    }, children: [Math.abs(v).toLocaleString(), "%"] }), _jsx("img", { src: isPos ? "/assets/trend-up.svg" : "/assets/trending-down.svg", alt: "", style: { width: 12, height: 12 } })] }));
                    })() : options?.isChip && value ? (options.showTooltip ? (_jsx(HoverTooltip, { title: _jsx(Box, { children: value }), arrow: true, placement: "top", children: _jsx(Chip, { label: value, size: "small", sx: {
                                height: '24px',
                                backgroundColor: options.chipColor || '#ECFDF3',
                                color: '#027A48',
                                fontWeight: 500,
                                cursor: 'pointer',
                            } }) })) : (_jsx(Chip, { label: value, size: "small", sx: {
                            height: '24px',
                            backgroundColor: options.chipColor || '#ECFDF3',
                            color: '#027A48',
                            fontWeight: 500,
                        } }))) : Array.isArray(value) ? (_jsx(Box, { sx: { display: 'flex', gap: 0.5, flexWrap: 'wrap' }, children: value.length ? (value.map((item, idx) => (_jsx(Chip, { label: item, size: "small", sx: {
                                height: '24px',
                                backgroundColor: '#EEF4FF',
                                color: '#3538CD',
                                fontWeight: 500,
                            } }, idx)))) : (_jsx(Typography, { sx: valueStyle, children: "-" })) })) : (_jsx(Typography, { sx: { ...valueStyle, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }, children: displayValue })) })] }));
    };
    const cardBoxStyle = {
        border: '1px solid #EAECF0',
        borderRadius: '8px',
        p: 1,
        backgroundColor: '#FCFCFD',
        height: '100%',
        alignSelf: 'stretch',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        // boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.04)',
    };
    const renderPaidMediaGoalContent = () => (_jsxs(Box, { sx: {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1,
            alignItems: 'stretch',
        }, children: [_jsxs(Box, { sx: cardBoxStyle, children: [renderField('Spend Goal', data.paid_spend_goal, {
                        labelWidth: paidBox1LabelWidth,
                        isCurrency: true
                    }), renderField('MP(%)', data.paid_marketing_per, {
                        isPercent: true,
                        labelWidth: paidBox1LabelWidth
                    }), renderField('ROAS', summary.paid_roas, { labelWidth: paidBox1LabelWidth }), renderField('Clockify Goal', data.clockify_goal ? `${data.clockify_goal} hrs` : null, { labelWidth: paidBox1LabelWidth }), data.paid_site_spend_goal ? renderField('Site Spend Goal', data.paid_site_spend_goal, { labelWidth: paidBox1LabelWidth, isCurrency: true }) : null, data.paid_total_site_rev ? renderField('Site Rev', data.paid_total_site_rev, { labelWidth: paidBox1LabelWidth, isCurrency: true }) : null, data.paid_site_roas_final ? renderField('Site Roas', data.paid_site_roas_final, { labelWidth: paidBox1LabelWidth }) : null] }), _jsxs(Box, { sx: cardBoxStyle, children: [renderField('Social Spend %', data.paid_meta_spend_per, {
                        isPercent: true,
                        labelWidth: paidBox2LabelWidth
                    }), renderField('Search Spend %', data.paid_google_spend_per, {
                        isPercent: true,
                        labelWidth: paidBox2LabelWidth
                    }), renderField('Bing Spend %', data.paid_bing_spend_per, {
                        isPercent: true,
                        labelWidth: paidBox2LabelWidth
                    }), renderField('Criteo Spend %', data.paid_criteo_spend_per, {
                        isPercent: true,
                        labelWidth: paidBox2LabelWidth
                    }), renderField('Increase % when ROAS is up', data.paid_roas_up_increase_per, {
                        isPercent: true,
                        labelWidth: paidBox2LabelWidth
                    }), renderField('Spend modification if ROAS is down', data.paid_roas_down_adjust_per, {
                        isPercent: true,
                        labelWidth: paidBox2LabelWidth
                    }), renderField('Account Priority', data.account_priority, {
                        isChip: true,
                        chipColor: '#FFF7ED',
                        labelWidth: paidBox1LabelWidth
                    })] })] }));
    const renderAffiliateGoalContent = () => (_jsxs(Box, { sx: {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1,
            alignItems: 'stretch',
        }, children: [_jsxs(Box, { sx: cardBoxStyle, children: [renderField('Site Prev. Year Rev', data.aff_site_prev_year_rev, {
                        labelWidth: affiliateBox1LabelWidth,
                        isCurrency: true
                    }), renderField('Site YOY Growth Rate', data.aff_site_yoy_growth_rate, {
                        isGrowthRate: true,
                        labelWidth: affiliateBox1LabelWidth
                    }), renderField('Site Proj Rev', data.aff_site_projected_revenue, {
                        labelWidth: affiliateBox1LabelWidth,
                        isCurrency: true
                    }), renderField('Site Measurement Channel', data.aff_site_measurement_channel, {
                        labelWidth: affiliateBox1LabelWidth
                    }), renderField('ROAS', summary.aff_roas, { labelWidth: affiliateBox1LabelWidth }), renderField('AM Projection', data.aff_am_projected_revenue, {
                        labelWidth: affiliateBox1LabelWidth,
                        isCurrency: true
                    }), data.aff_full_network_goal != null && data.aff_full_network_goal !== '' && data.aff_full_network_goal !== 0 && renderField('Site Goal', data.aff_full_network_goal, {
                        labelWidth: commissionPaidBox1LabelWidth,
                        isCurrency: true
                    })] }), _jsxs(Box, { sx: cardBoxStyle, children: [renderField('Outreach', data.aff_outreach_goal, {
                        labelWidth: affiliateBox2LabelWidth
                    }), renderField('Intro to AM', data.aff_intro_am_goal, {
                        labelWidth: affiliateBox2LabelWidth
                    }), renderField('Conv. Rate', data.aff_conv_rate_goal, {
                        isPercent: true,
                        labelWidth: affiliateBox2LabelWidth
                    }), renderField('Conversations', data.aff_conversations_goal, {
                        labelWidth: affiliateBox2LabelWidth
                    }), renderField('Published', data.aff_published_goal, {
                        labelWidth: affiliateBox2LabelWidth
                    }), renderField('Clockify Goal', data.clockify_goal ? `${data.clockify_goal} hrs` : null, { labelWidth: affiliateBox2LabelWidth })] })] }));
    const renderPaidMediaCommissionContent = () => (_jsxs(Box, { sx: {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1,
            alignItems: 'stretch',
        }, children: [_jsxs(Box, { sx: cardBoxStyle, children: [renderField('Days in Month (Comm)', summary.comm_days_in_month, {
                        labelWidth: commissionPaidBox1LabelWidth
                    }), renderField('Days Billed in Month (Comm)', summary.comm_days_billed_in_month, {
                        labelWidth: commissionPaidBox1LabelWidth
                    }), renderField('Days in Month (Mgmt)', summary.mgmt_days_in_month, {
                        labelWidth: commissionPaidBox1LabelWidth
                    }), renderField('Days Billed in Month (Mgmt)', summary.mgmt_days_billed_in_month, {
                        labelWidth: commissionPaidBox1LabelWidth
                    }), renderField('Full Management Fees', summary.full_mgmt_fees, {
                        labelWidth: commissionPaidBox1LabelWidth,
                        isCurrency: true
                    }), renderField('Billable Management Fees', summary.billable_mgmt_fees, {
                        labelWidth: commissionPaidBox1LabelWidth,
                        isCurrency: true
                    }), data.paid_revenue_threshold != null && data.paid_revenue_threshold !== '' && data.paid_revenue_threshold !== 0 && renderField('Revenue Threshold', data.paid_revenue_threshold, {
                        labelWidth: commissionPaidBox1LabelWidth,
                        isCurrency: true
                    }), data.paid_spend_threshold != null && data.paid_spend_threshold !== '' && data.paid_spend_threshold !== 0 && renderField('Spend Threshold', data.paid_spend_threshold, {
                        labelWidth: commissionPaidBox1LabelWidth,
                        isCurrency: true
                    }), data.paid_spend_goal != null && data.paid_spend_goal !== '' && data.paid_spend_goal !== 0 && renderField('Spend Goal', data.paid_spend_goal, {
                        labelWidth: commissionPaidBox1LabelWidth,
                        isCurrency: true
                    })] }), _jsxs(Box, { sx: cardBoxStyle, children: [renderField('Affiliate %', summary.affiliate_per, {
                        isPercent: true,
                        labelWidth: commissionPaidBox2LabelWidth
                    }), renderField('Paid Social %', summary.paid_social_per, {
                        isPercent: true,
                        labelWidth: commissionPaidBox2LabelWidth
                    }), renderField('Paid Search %', summary.paid_search_per, {
                        isPercent: true,
                        labelWidth: commissionPaidBox2LabelWidth
                    }), renderField('Account Management %', summary.account_management_per, {
                        isPercent: true,
                        labelWidth: commissionPaidBox2LabelWidth
                    }), renderField('Email / SMS %', summary.email_sms_per, {
                        isPercent: true,
                        labelWidth: commissionPaidBox2LabelWidth
                    }), renderField('Notes', data.paid_notes, {
                        labelWidth: commissionPaidBox2LabelWidth
                    })] })] }));
    const renderAffiliateCommissionContent = () => (_jsxs(Box, { sx: {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1,
            alignItems: 'stretch',
        }, children: [_jsxs(Box, { sx: cardBoxStyle, children: [renderField('Days in Month (Comm)', summary.comm_days_in_month, {
                        labelWidth: commissionAffiliateBox1LabelWidth
                    }), renderField('Days Billed in Month (Comm)', summary.comm_days_billed_in_month, {
                        labelWidth: commissionAffiliateBox1LabelWidth
                    }), renderField('Days in Month (Mgmt)', summary.mgmt_days_in_month, {
                        labelWidth: commissionAffiliateBox1LabelWidth
                    }), renderField('Days Billed in Month (Mgmt)', summary.mgmt_days_billed_in_month, {
                        labelWidth: commissionAffiliateBox1LabelWidth
                    }), renderField('Full Management Fees', summary.full_mgmt_fees, {
                        labelWidth: commissionAffiliateBox1LabelWidth,
                        isCurrency: true
                    }), renderField('Billable Management Fees', summary.billable_mgmt_fees, {
                        labelWidth: commissionAffiliateBox1LabelWidth,
                        isCurrency: true
                    })] }), _jsxs(Box, { sx: cardBoxStyle, children: [renderField('Commission Baseline', data.aff_comm_baseline, {
                        labelWidth: commissionAffiliateBox2LabelWidth,
                        isCurrency: !isAffOrderBase,
                        isFormattedNumber: isAffOrderBase
                    }), data.aff_order_goal != null && data.aff_order_goal !== '' && data.aff_order_goal !== 0 && renderField('Order Goal', data.aff_order_goal, {
                        labelWidth: commissionAffiliateBox2LabelWidth,
                        // isCurrency: true
                    }), renderField('Affiliate %', summary.affiliate_per, {
                        isPercent: true,
                        labelWidth: commissionAffiliateBox2LabelWidth
                    }), renderField('Paid Social %', summary.paid_social_per, {
                        isPercent: true,
                        labelWidth: commissionAffiliateBox2LabelWidth
                    }), renderField('Paid Search %', summary.paid_search_per, {
                        isPercent: true,
                        labelWidth: commissionAffiliateBox2LabelWidth
                    }), renderField('Account Management %', summary.account_management_per, {
                        isPercent: true,
                        labelWidth: commissionAffiliateBox2LabelWidth
                    }), renderField('Email / SMS %', summary.email_sms_per, {
                        isPercent: true,
                        labelWidth: commissionAffiliateBox2LabelWidth
                    }), renderField('Notes', data.affiliate_notes, {
                        labelWidth: commissionAffiliateBox2LabelWidth
                    })] })] }));
    const renderPaidMediaAMProjectionContent = () => {
        const prevLabel = getMonthLabel(currentYearMonth, -1);
        const currLabel = getMonthLabel(currentYearMonth, 0);
        const lw = '200px';
        return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Box, { sx: {
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 1,
                        alignItems: 'stretch',
                    }, children: [_jsxs(Box, { sx: cardBoxStyle, children: [renderField(`${getMonthLabel(currentYearMonth, -1).split(' ')[0]} ${Number(getMonthLabel(currentYearMonth, -1).split(' ')[1]) - 1} Rev`, summary.paid_prev_month_revenue, { isCurrency: true, labelWidth: lw }), renderField(`${prevLabel} Rev (Actual)`, prevTrendingRev, { isCurrency: true, labelWidth: lw, }), renderField(`${prevLabel} Rev Projection`, summary.paid_prev_month_projection, { isCurrency: true, labelWidth: lw }), renderField(`${prevLabel} ROAS or Spend Goal`, data.prev_month_roas_or_spend, { labelWidth: lw })] }), _jsxs(Box, { sx: cardBoxStyle, children: [renderField(`${getMonthLabel(currentYearMonth, 0).split(' ')[0]} ${Number(getMonthLabel(currentYearMonth, 0).split(' ')[1]) - 1} Rev`, summary.paid_current_month_revenue, { isCurrency: true, labelWidth: lw }), renderField(`${currLabel} Rev (trending)`, currTrendingRev, { isCurrency: true, labelWidth: lw }), renderField('LY MOM Growth Rate', data.paid_ly_mom_growth_rate, { isGrowthRate: true, labelWidth: lw }), renderField('YOY Growth Rate', data.paid_yoy_growth_rate, { isGrowthRate: true, labelWidth: lw }), renderField(`${currLabel} Rev Projection`, data.paid_projected_revenue, { isCurrency: true, labelWidth: lw })] }), _jsxs(Box, { sx: cardBoxStyle, children: [renderField(`${currLabel} ROAS/Spend`, data.current_month_roas_or_spend, { labelWidth: lw, isEditable: true, editField: 'current_month_roas_or_spend', disabled: isClientInactive, disabledTooltip: 'Client is inactive' }), renderField('AM Rev Changes', editData.paid_changes_revenue, { labelWidth: lw, isEditable: true, editField: 'paid_changes_revenue', disabled: isClientInactive, disabledTooltip: 'Client is inactive' }), renderField('Paid Social Spend %', data.paid_social_spend_per, { isPercent: true, labelWidth: lw }), renderField('Paid Search Spend %', data.paid_search_spend_per, { isPercent: true, labelWidth: lw }), renderField('Bing Spend %', data.bing_spend_per, { isPercent: true, labelWidth: lw })] })] }), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mt: 1 }, children: _jsx(Tooltip, { title: isClientInactive ? 'Client is inactive' : '', placement: "top", arrow: true, disableHoverListener: !isClientInactive, children: _jsx("span", { children: _jsx(Button, { variant: "contained", size: "small", startIcon: isSaving ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : _jsx(Save, { size: 16 }), onClick: handleSave, disabled: isSaving || isClientInactive, sx: {
                                    backgroundColor: '#FF008E',
                                    '&:hover': { backgroundColor: '#e0007c' },
                                    border: '1px solid #A3005C',
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    px: 2,
                                    height: '36px'
                                }, children: isSaving ? 'Saving...' : 'Save Changes' }) }) }) })] }));
    };
    const renderAffiliateAMProjectionContent = () => {
        const prevLabel = getMonthLabel(currentYearMonth, -1);
        const currLabel = getMonthLabel(currentYearMonth, 0);
        const lw = '200px';
        return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(Box, { sx: {
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 1,
                        alignItems: 'stretch',
                    }, children: [_jsxs(Box, { sx: cardBoxStyle, children: [renderField(`${getMonthLabel(currentYearMonth, -1).split(' ')[0]} ${Number(getMonthLabel(currentYearMonth, -1).split(' ')[1]) - 1} Rev`, summary.aff_prev_month_revenue, { isCurrency: true, labelWidth: lw }), renderField(`${prevLabel} Rev (Actual)`, data.previous_month_actual_revenue?.revenue, { isCurrency: true, labelWidth: lw, }), renderField(`${prevLabel} Rev AM Projection`, summary.aff_prev_month_am_projection, { isCurrency: true, labelWidth: lw }), renderField(`${prevLabel} Rev Projection`, summary.aff_prev_month_projection, { isCurrency: true, labelWidth: lw })] }), _jsxs(Box, { sx: cardBoxStyle, children: [renderField(`${getMonthLabel(currentYearMonth, 0).split(' ')[0]} ${Number(getMonthLabel(currentYearMonth, 0).split(' ')[1]) - 1} Rev`, summary.aff_current_month_revenue, { isCurrency: true, labelWidth: lw }), renderField('LY MOM Growth Rate', data.aff_ly_mom_growth_rate, { isGrowthRate: true, labelWidth: lw }), renderField('YOY Growth Rate', data.aff_yoy_growth_rate, { isGrowthRate: true, labelWidth: lw }), renderField(`${currLabel} Rev Projection `, data.aff_projected_revenue, { isCurrency: true, labelWidth: lw })] }), _jsxs(Box, { sx: cardBoxStyle, children: [renderField('AM Rev Changes to Projection', editData.aff_am_projected_revenue, { labelWidth: lw, isEditable: true, editField: 'aff_am_projected_revenue', disabled: isClientInactive, disabledTooltip: 'Client is inactive' }), renderField('Budget?', editData.aff_budget_toggle, { labelWidth: lw, isToggle: true, editField: 'aff_budget_toggle', disabled: isClientInactive, disabledTooltip: 'Client is inactive' }), renderField('Samples?', editData.aff_samples_toggle, { labelWidth: lw, isToggle: true, editField: 'aff_samples_toggle', disabled: isClientInactive, disabledTooltip: 'Client is inactive' })] })] }), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mt: 1 }, children: _jsx(Tooltip, { title: isClientInactive ? 'Client is inactive' : '', placement: "top", arrow: true, disableHoverListener: !isClientInactive, children: _jsx("span", { children: _jsx(Button, { variant: "contained", size: "small", startIcon: isSaving ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : _jsx(Save, { size: 16 }), onClick: handleSave, disabled: isSaving || isClientInactive, sx: {
                                    backgroundColor: '#7F56D9',
                                    '&:hover': { backgroundColor: '#6941C6' },
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    px: 2
                                }, children: isSaving ? 'Saving...' : 'Save Changes' }) }) }) })] }));
    };
    const renderPaidMediaContent = () => {
        if (goalType === 'commission_goal') {
            return renderPaidMediaCommissionContent();
        }
        if (goalType === 'am_projection') {
            return renderPaidMediaAMProjectionContent();
        }
        return renderPaidMediaGoalContent();
    };
    const renderAffiliateContent = () => {
        if (goalType === 'commission_goal') {
            return renderAffiliateCommissionContent();
        }
        if (goalType === 'am_projection') {
            return renderAffiliateAMProjectionContent();
        }
        return renderAffiliateGoalContent();
    };
    return (_jsx(Box, { sx: {
            p: 1,
            bgcolor: "#F9FAFB",
            borderTop: "1px solid #f1f5f9",
            borderBottom: "1px solid #f1f5f9",
        }, children: _jsxs(Box, { sx: {
                display: "flex",
                flexDirection: "column",
                gap: 1,
                border: showTabs ? "1px solid #e5e7eb" : "none",
                borderRadius: "8px",
                overflow: 'hidden',
                bgcolor: 'white',
                boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)'
            }, children: [showTabs && (_jsx(Box, { sx: { borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }, children: _jsx(ChannelTabs, { tabs: tabItems, value: subTab, onChange: (key) => setSubTab(key), tabstyle: "short" }) })), _jsxs(Box, { sx: { p: 1 }, children: [subTab === 'paid_media' && renderPaidMediaContent(), subTab === 'affiliate' && renderAffiliateContent()] })] }) }));
};
export default GoalExpandedContent;
